import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { RaceRecording } from '../../shared/race';
import { displayTime } from '../../shared/replay';
import { hasCoverage, type BufferedRange } from '../../shared/session-replay';
import { anchorEndedTail, endedHead, type EndedTail } from '../../shared/display-clock';
import { changePlaybackRate, createPlaybackClock, pausePlaybackClock, resumePlaybackClock, seekPlaybackClock, tickPlaybackClock, type PlaybackClock, type PlaybackWindow } from '../../shared/playback-clock';
import { createSnapshotProjector } from '../../shared/snapshot-projector';
import { retainLiveReviewWindow, type LiveReviewWindow } from '../../shared/live-review-window';

interface PlaybackOptions { ranges?: BufferedRange[]; initialAtMs?: number; startMs?: number; resetKey?: number; static?: boolean; liveEnded?: boolean; retainLiveReview?: boolean }
export function usePlayback(recording: RaceRecording, live: boolean, options: PlaybackOptions = {}) {
  const [delay, setDelay] = useState(0);
  const { ranges, initialAtMs = recording.session.source === 'demo' ? 240_000 : 0, startMs = 0, resetKey = 0 } = options;
  const retainedReview = useRef<LiveReviewWindow | null>(null);
  retainedReview.current = retainLiveReviewWindow(retainedReview.current, recording, startMs, Boolean(options.retainLiveReview && !live), resetKey);
  const reviewRecording = retainedReview.current?.recording ?? recording;
  const tail = useRef<EndedTail | null>(null);
  const [tailHead, setTailHead] = useState(0);
  const tailPosition = useRef(0);
  // The live display watermark remains independent of a pinned review window.
  const liveClock = options.liveEnded && tail.current?.key === recording.session.key ? Math.max(recording.durationMs, tailHead) : recording.durationMs;
  const liveDisplayHeadMs = Math.min(recording.durationMs, displayTime(liveClock, delay));
  const windowStartMs = retainedReview.current?.startMs ?? startMs;
  const windowEndMs = retainedReview.current ? Math.min(reviewRecording.durationMs, liveDisplayHeadMs) : reviewRecording.durationMs;
  const environment = useRef<PlaybackWindow>({ durationMs: windowEndMs });
  environment.current = { durationMs: windowEndMs, startMs: windowStartMs, delaySeconds: delay, ranges, suspended: live || options.static };
  const [state, setState] = useState(() => createPlaybackClock(initialAtMs, performance.now(), environment.current, !options.static));
  const cursor = useRef(state);
  const sessionKey = useRef('');
  const [seekRevision, setSeekRevision] = useState(0);
  const project = useMemo(createSnapshotProjector, []);

  const commit = useCallback((next: PlaybackClock) => {
    const previous = cursor.current;
    cursor.current = next;
    if (previous.headMs !== next.headMs || previous.playing !== next.playing || previous.rate !== next.rate) setState(next);
  }, []);
  const commitTail = useCallback((head: number) => {
    if (tailPosition.current === head) return;
    tailPosition.current = head; setTailHead(head);
  }, []);
  const currentWindow = useCallback((): PlaybackWindow => ({ ...environment.current, suspended: environment.current.suspended || document.hidden }), []);

  useEffect(() => {
    const identity = `${recording.session.key}:${recording.session.dateStart}:${resetKey}`;
    if (sessionKey.current !== identity) {
      sessionKey.current = identity;
      commit(createPlaybackClock(initialAtMs, performance.now(), environment.current, !options.static));
      setSeekRevision(value => value + 1);
    }
  }, [recording.session.key, recording.session.dateStart, initialAtMs, resetKey, options.static, commit]);

  useEffect(() => {
    const next = tickPlaybackClock(cursor.current, performance.now(), { ...environment.current, suspended: true });
    commit(options.static ? { ...next, playing: false } : next);
  }, [live, options.static, windowEndMs, ranges, windowStartMs, delay, commit]);

  useEffect(() => {
    if (!options.liveEnded) { tail.current = null; commitTail(0); return; }
    const currentTime = performance.now();
    tail.current = anchorEndedTail(tail.current, recording.session.key, recording.durationMs, currentTime);
    commitTail(Math.min(tail.current.head, recording.durationMs + delay * 1000));
  }, [options.liveEnded, recording.durationMs, recording.session.key, commitTail]);

  useEffect(() => {
    let frame = 0, disposed = false;
    const tailLimit = recording.durationMs + delay * 1000;
    const replayActive = () => {
      const window = currentWindow(), atMs = displayTime(cursor.current.headMs, window.delaySeconds ?? 0);
      return !window.suspended && cursor.current.playing && atMs < window.durationMs
        && (window.ranges === undefined || hasCoverage(window.ranges, atMs, window.durationMs));
    };
    const tailActive = () => options.liveEnded && tail.current?.key === recording.session.key;
    const advance = () => {
      frame = 0;
      if (disposed || document.hidden) return;
      const now = performance.now();
      if (replayActive()) commit(tickPlaybackClock(cursor.current, now, currentWindow()));
      // Keep the full elapsed-time anchor, but stop publishing once this display delay drains.
      if (tailActive()) commitTail(Math.min(tailLimit, endedHead(tail.current!, recording.durationMs, now)));
      if (replayActive() || tailActive() && tailPosition.current < tailLimit) frame = requestAnimationFrame(advance);
    };
    // Returning to the tab keeps the user's paused cursor instead of catching up in one jump.
    const onVisibility = () => {
      cancelAnimationFrame(frame); frame = 0;
      const next = tickPlaybackClock(cursor.current, performance.now(), { ...environment.current, suspended: true });
      commit(document.hidden ? { ...next, playing: false } : next);
      if (!document.hidden) advance();
    };
    advance();
    document.addEventListener('visibilitychange', onVisibility);
    return () => { disposed = true; cancelAnimationFrame(frame); document.removeEventListener('visibilitychange', onVisibility); };
  }, [commit, commitTail, currentWindow, live, options.static, options.liveEnded, state.playing, seekRevision, delay, ranges, recording.durationMs, recording.session.key, windowEndMs]);

  // The live display watermark also advances while inspecting earlier laps.
  // Ending a session drains the viewing delay without truncating already seen history.
  const clock = live ? liveClock : Math.min(state.headMs, windowEndMs + delay * 1000);
  const atMs = Math.min(windowEndMs, displayTime(clock, delay));
  const buffering = !live && ranges !== undefined && !hasCoverage(ranges, atMs, windowEndMs);
  // Loading is a transport state. It must not replace the known event ledger
  // with an empty recording, otherwise every panel loses its last-known state.
  const { snapshot, dataRevision } = project(reviewRecording, atMs);
  const ended = !live && atMs >= windowEndMs;
  const seek = useCallback((ms: number) => {
    commit(seekPlaybackClock(cursor.current, ms, performance.now(), environment.current));
    setSeekRevision(value => value + 1);
  }, [commit]);
  const playFrom = useCallback((ms: number) => {
    commit(seekPlaybackClock(cursor.current, ms, performance.now(), environment.current, true));
    setSeekRevision(value => value + 1);
  }, [commit]);
  const pause = useCallback(() => commit(pausePlaybackClock(cursor.current, performance.now(), currentWindow())), [commit, currentWindow]);
  const setRate = useCallback((rate: number) => commit(changePlaybackRate(cursor.current, rate, performance.now(), currentWindow())), [commit, currentWindow]);
  const togglePlaying = useCallback(() => {
    if (displayTime(cursor.current.headMs, environment.current.delaySeconds ?? 0) >= environment.current.durationMs) {
      playFrom(environment.current.startMs ?? 0); return;
    }
    commit(cursor.current.playing ? pausePlaybackClock(cursor.current, performance.now(), currentWindow()) : resumePlaybackClock(cursor.current, performance.now(), environment.current));
  }, [commit, currentWindow, playFrom]);
  // The pinned review window is part of the read model. Consumers must use the
  // same recording that produced `snapshot`; otherwise a live review can keep
  // an old cursor while the chart and records read from the rolling live buffer.
  return { recording: reviewRecording, snapshot, positionMs: atMs, liveDisplayHeadMs, windowStartMs, windowEndMs, playing: !options.static && state.playing && !ended, ended, buffering, rate: state.rate, setRate, delay, setDelay, seek, pause, playFrom, togglePlaying, seekRevision, dataRevision };
}
